import { useCallback, useEffect, type FC } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight";

export type LightboxImage = {
    src: string;
    alt: string;
};

type ImageLightboxProps = {
    images: LightboxImage[];
    currentIndex: number;
    onIndexChange: (index: number) => void;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export const ImageLightbox: FC<ImageLightboxProps> = ({ images, currentIndex, onIndexChange, open, onOpenChange }) => {
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < images.length - 1;

    const goToPrev = useCallback(() => {
        if (hasPrev) {
            onIndexChange(currentIndex - 1);
        }
    }, [hasPrev, currentIndex, onIndexChange]);

    const goToNext = useCallback(() => {
        if (hasNext) {
            onIndexChange(currentIndex + 1);
        }
    }, [hasNext, currentIndex, onIndexChange]);

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault();
                goToPrev();
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                goToNext();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, goToPrev, goToNext]);

    const current = images[currentIndex];
    if (!current) return null;

    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                <DialogPrimitive.Content
                    className="fixed inset-0 z-50 flex items-center justify-center p-8 focus:outline-none"
                    aria-label="Image preview"
                    data-tracking-id="image-lightbox-overlay"
                    onClick={() => onOpenChange(false)}
                >
                    {/* Header with filename and counter */}
                    {/* eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- stopPropagation only, not a tracked interaction */}
                    <div
                        className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <DialogPrimitive.Title className="truncate text-sm font-medium text-white">
                            {current.alt || "Image"}
                        </DialogPrimitive.Title>
                        {images.length > 1 && (
                            <DialogPrimitive.Description className="shrink-0 text-sm text-white/70">
                                {currentIndex + 1} of {images.length}
                            </DialogPrimitive.Description>
                        )}
                        {images.length <= 1 && (
                            <DialogPrimitive.Description className="sr-only">Image preview</DialogPrimitive.Description>
                        )}
                    </div>

                    {/* Previous button */}
                    {hasPrev && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                goToPrev();
                            }}
                            className="absolute left-4 z-10 flex size-10 items-center justify-center rounded-full bg-surface-primary/80 text-content-primary shadow-md transition-colors hover:bg-surface-primary"
                            aria-label="Previous image"
                            data-tracking-id="image-lightbox-previous"
                        >
                            <IconChevronRight size="base" className="rotate-180" />
                        </button>
                    )}

                    {/* Image */}
                    <img
                        src={current.src}
                        alt={current.alt}
                        className="max-h-full max-w-full rounded-lg object-contain"
                        data-tracking-id="image-lightbox-image"
                        onClick={(e) => e.stopPropagation()}
                    />

                    {/* Next button */}
                    {hasNext && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                goToNext();
                            }}
                            className="absolute right-4 z-10 flex size-10 items-center justify-center rounded-full bg-surface-primary/80 text-content-primary shadow-md transition-colors hover:bg-surface-primary"
                            aria-label="Next image"
                            data-tracking-id="image-lightbox-next"
                        >
                            <IconChevronRight size="base" />
                        </button>
                    )}
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};
